import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/axiosConfig';
import io from 'socket.io-client';
import { formatBrasiliaDate } from '../utils/dateUtils';
import { playBeep } from '../utils/audioUtils';
import './Painel.css';
import './TableStyles.css';
const Painel = () => {
  const [trucks, setTrucks] = useState([]);
  const [summary, setSummary] = useState({ aguardando: 0, emDescarga: 0, concluido: 0, volumeTotalDescarga: 0 });
  const [rotationOffset, setRotationOffset] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const previousTruckStatuses = useRef({});
  const socketRef = useRef(null);
  // Play alarm sound
  const playAlarm = useCallback(() => {
    try {
      playBeep(1000, 800); // 1 second, 800Hz beep
    } catch (error) {
      console.log('Error playing alarm:', error);
    }
  }, []);
  // Check for status changes to trigger alarm
  const checkForStatusChanges = useCallback((currentTrucks) => {
    currentTrucks.forEach(truck => {
      const previousStatus = previousTruckStatuses.current[truck._id];
      if (previousStatus && previousStatus !== 'CONCLUIDO' && truck.status === 'CONCLUIDO') {
        // Truck just changed to CONCLUIDO status - play alarm
        playAlarm();
      }
      // Also trigger alarm when truck enters AGUARDANDO_DESCARREGAMENTO status
      if (previousStatus && previousStatus !== 'AGUARDANDO_DESCARREGAMENTO' && truck.status === 'AGUARDANDO_DESCARREGAMENTO') {
        // Truck just changed to AGUARDANDO_DESCARREGAMENTO status - play alarm
        playAlarm();
      }
      previousTruckStatuses.current[truck._id] = truck.status;
    });
  }, [playAlarm]);
  // Process truck data and update state
  const processTruckData = useCallback((allTrucks) => {
    // Filter for DESCARREGAMENTO type trucks that are not checked out
    const descargaTrucks = allTrucks.filter(truck => 
      (truck.tipo === 'DESCARREGAMENTO' || truck.tipo === 'CARREGAMENTO') && 
      truck.status !== 'CHECKOUT'
    );
    // Sort trucks: concluido first, then em descarga, then aguardando
    const sortedTrucks = [...descargaTrucks].sort((a, b) => {
      // Priority order: CONCLUIDO > EM_DESCARGA > AGUARDANDO_DESCARREGAMENTO > AGUARDANDO
      const statusPriority = { 
        'CONCLUIDO': 1, 
        'EM_DESCARGA': 2, 
        'AGUARDANDO_DESCARREGAMENTO': 3,
        'AGUARDANDO': 4 
      };
      return statusPriority[a.status] - statusPriority[b.status];
    });
    setTrucks(sortedTrucks);
    // Calculate summary - only count non-checked out trucks
    const aguardando = descargaTrucks.filter(t => t.status === 'AGUARDANDO').length;
    const emDescarga = descargaTrucks.filter(t => t.status === 'EM_DESCARGA').length;
    const concluido = descargaTrucks.filter(t => t.status === 'CONCLUIDO').length;
    const aguardandoCarregamento = descargaTrucks.filter(t => t.status === 'AGUARDANDO_DESCARREGAMENTO').length;
    const volumeTotalDescarga = descargaTrucks
      .filter(t => t.status === 'EM_DESCARGA')
      .reduce((acc, t) => acc + (Number(t.volume) || 0), 0);
    setSummary({ aguardando, emDescarga, concluido, aguardandoCarregamento, volumeTotalDescarga });
    // Check for status changes to trigger alarm
    checkForStatusChanges(descargaTrucks);
  }, [checkForStatusChanges]);
  // Fetch trucks data
  const fetchTrucks = useCallback(async () => {
    try {
      const response = await api.get('/trucks');
      processTruckData(response.data);
    } catch (error) {
      console.error('Error fetching trucks:', error);
    }
  }, [processTruckData]);
  // Initialize socket connection
  useEffect(() => {
    socketRef.current = io('https://nblpatio.nbl.dev.br');
    socketRef.current.on('connect', () => {
      console.log('Painel: Socket connected');
    });
    socketRef.current.on('truckUpdated', (updatedTruck) => {
      console.log('Painel: Received truck update via socket', updatedTruck);
      // Refresh truck data when a truck is updated
      fetchTrucks();
    });
    socketRef.current.on('truckDeleted', (deletedTruck) => {
      console.log('Painel: Received truck deletion via socket', deletedTruck);
      // Refresh truck data when a truck is deleted
      fetchTrucks();
    });
    socketRef.current.on('disconnect', () => {
      console.log('Painel: Socket disconnected');
    });
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [fetchTrucks]);
  // Fetch data on component mount
  useEffect(() => {
    fetchTrucks();
    const interval = setInterval(fetchTrucks, 30000); // Refresh every 30 seconds as fallback
    return () => {
      clearInterval(interval);
    };
  }, [fetchTrucks]);
  // Initialize previous statuses
  useEffect(() => {
    const initialStatuses = {};
    trucks.forEach(truck => {
      initialStatuses[truck._id] = truck.status;
    });
    previousTruckStatuses.current = initialStatuses;
  }, [trucks]);
  // Auto-rotate trucks list every 5 seconds
  useEffect(() => {
    if (!autoRotate || trucks.length <= 10) return;
    const rotationInterval = setInterval(() => {
      setRotationOffset(prev => (prev + 1) % trucks.length);
    }, 12000);
    return () => clearInterval(rotationInterval);
  }, [autoRotate, trucks.length]);
  // Get rotated trucks list for display
  const getRotatedTrucks = () => {
    if (trucks.length === 0) return [];
    const rotated = [...trucks];
    for (let i = 0; i < rotationOffset; i++) {
      rotated.push(rotated.shift());
    }
    return rotated;
  };
  // Toggle fullscreen mode
    const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Erro ao entrar em tela cheia: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Efeito para sincronizar caso o usuário use a tecla ESC
  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);
  // Get status display info
  const getStatusInfo = (status) => {
    switch (status) {
      case 'AGUARDANDO':
        return { text: 'Aguardando Descarga', color: 'blue' };
      case 'EM_DESCARGA':
        return { text: 'Em Descarga', color: 'yellow' };
      case 'AGUARDANDO_DESCARREGAMENTO':
        return { text: 'Aguardando Carregamento', color: 'cyan' };
      case 'CONCLUIDO':
        return { text: 'Concluído - Retirar do pátio', color: 'red' };
      default:
        return { text: status, color: 'gray' };
    }
  };
  // Get waiting order number (based on entry time)
  const getWaitingOrder = (truck, trucks) => {
    if (truck.status !== 'AGUARDANDO') return '';
    // Get all waiting trucks sorted by entry time
    const waitingTrucks = trucks
      .filter(t => t.status === 'AGUARDANDO')
      .sort((a, b) => new Date(a.entryTime) - new Date(b.entryTime));
    // Find the position of this truck
    const index = waitingTrucks.findIndex(t => t._id === truck._id);
    return index >= 0 ? index + 1 : '';
  };
  // Get dock number for trucks in unloading status
  const getDockNumber = (truck) => {
    if (truck.status !== 'EM_DESCARGA') return '';
    // Check different possible dock properties
    if (truck.dock && truck.dock.number) return truck.dock.number;
    if (truck.dockNumber) return truck.dockNumber;
    if (truck.dock && truck.dock.name) return truck.dock.name;
    if (truck.dockId) return truck.dockId;
    return '';
  };
  return (
    <div className={`painel-container ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* Fullscreen toggle button */}
                <div className="fullscreen-toggle">
        <button onClick={toggleFullscreen} className="fullscreen-button" title={isFullscreen ? "Sair da Tela Inteira" : "Tela Inteira"}>
          {isFullscreen ? (
            <span className="button-icon" style={{ margin: 0 }}>🗗</span>
          ) : (
            <>
              <span className="button-icon">🖥️</span>
              Tela Inteira
            </>
          )}
        </button>

        {!isFullscreen && (
          <button onClick={() => setAutoRotate(!autoRotate)} className="rotation-button" title={autoRotate ? "Pausar Rotação" : "Retomar Rotação"}>
            {autoRotate ? (
              <><span className="button-icon">⏸️</span> Pausar Rotação</>
            ) : (
              <><span className="button-icon">▶️</span> Retomar Rotação</>
            )}
          </button>
        )}
      </div>

      {/* Compact Summary Counts */}
      <div className="summary-cards">
        <div className="summary-card blue">
          <div className="summary-count">{summary.aguardando}</div>
          <div className="summary-label">Aguardando Descarga</div>
        </div>
        <div className="summary-card yellow">
          <div className="summary-count">{summary.emDescarga}</div>
          <div className="summary-label">Em Descarga</div>
        </div>
        <div className="summary-card red">
          <div className="summary-count">{summary.concluido}</div>
          <div className="summary-label">Concluído</div>
        </div>
        <div className="summary-card cyan">
          <div className="summary-count">{summary.aguardandoCarregamento}</div>
          <div className="summary-label">Aguardando Carregamento</div>
        </div>
        <div className="summary-card purple">
          <div className="summary-count">{summary.volumeTotalDescarga.toLocaleString("pt-BR")}</div>
          <div className="summary-label">Volumetria em Descarregamento</div>
        </div>
      </div>

      {/* Trucks Table */}
      <div className="global-table-container">
        <table className="global-table">
          <thead>
            <tr>
              <th>Nº Ordem</th>
              <th>Placa Cavalo</th>
              <th>Placa Carreta</th>
              <th>Chegada</th>
              <th>Parceiro</th>
              <th>Doca</th>
              <th>Volume</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {getRotatedTrucks().map(truck => {
              const statusInfo = getStatusInfo(truck.status);
              // Add blinking class for completed status
              const isCompleted = truck.status === 'CONCLUIDO';
              const orderNumber = getWaitingOrder(truck, trucks);
              const dockNumber = getDockNumber(truck);
              return (
                <tr key={truck._id}>
                  <td>{orderNumber}</td>
                  <td className="plate-cell">{truck.truckPlate || 'N/A'}</td>
                  <td className="plate-cell">{truck.trailerPlate || 'N/A'}</td>
                  <td>{truck.entryTime ? formatBrasiliaDate(truck.entryTime) : 'N/A'}</td>
                  <td>{truck.partner?.name || truck.partner || 'N/A'}</td>
                  <td>{dockNumber}</td>
                  <td>{truck.volume ? Number(truck.volume).toLocaleString("pt-BR") : "-"}</td>
                  <td>
                    <span className={`status-badge ${statusInfo.color} ${isCompleted ? 'blinking' : ''}`}>
                      {statusInfo.text}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default Painel;